import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/AuthContext';
import Seo from '../../components/Seo';
import { Lock, LogIn, AlertCircle } from 'lucide-react';

export default function AdminLogin() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/admin';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await signIn(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err?.message || 'Unable to sign in. Please check your email and password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title="Admin Login" description="Chesapeake Counseling Center admin login." path="/admin/login" />
      <section className="min-h-[calc(100vh-200px)] flex items-center justify-center py-16 px-6" style={{ background: 'var(--warm-white)' }}>
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, var(--dusty-rose), #D08590)' }}>
              <Lock className="w-6 h-6 text-white" strokeWidth={1.6} />
            </div>
            <div className="eyebrow mb-2">Admin Access</div>
            <h1 className="font-serif-display text-3xl text-[color:var(--deep-sage)]">Sign in to your dashboard</h1>
            <p className="text-sm text-[color:var(--taupe-gray)] mt-2">
              Manage blog posts, drafts, and images for the Chesapeake Counseling Center website.
            </p>
          </div>

          <form onSubmit={onSubmit} className="card-soft p-6 md:p-8 space-y-4">
            {error && (
              <div className="rounded-xl px-4 py-3 flex items-start gap-2 text-sm" style={{ background: 'rgba(232,165,169,0.12)', color: '#8B3A44' }}>
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>{error}</div>
              </div>
            )}

            <div>
              <label htmlFor="admin-email" className="text-xs uppercase tracking-[0.2em] text-[color:var(--sage-dark)] font-semibold">Email</label>
              <input
                id="admin-email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full rounded-xl border bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--blush-pink)] transition"
                style={{ borderColor: 'var(--soft-border)' }}
              />
            </div>

            <div>
              <label htmlFor="admin-password" className="text-xs uppercase tracking-[0.2em] text-[color:var(--sage-dark)] font-semibold">Password</label>
              <input
                id="admin-password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full rounded-xl border bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--blush-pink)] transition"
                style={{ borderColor: 'var(--soft-border)' }}
              />
            </div>

            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full justify-center disabled:opacity-60"
            >
              <LogIn className="w-4 h-4" /> {busy ? 'Signing in…' : 'Sign in'}
            </button>

            <p className="text-xs text-center text-[color:var(--taupe-gray)] leading-relaxed">
              Forgot your password? Use the “Reset password” link in the Supabase dashboard or ask the site owner to reset it.
            </p>
          </form>

          <div className="text-center mt-6">
            <Link to="/" className="text-sm text-[color:var(--dusty-rose)] hover:underline">← Back to website</Link>
          </div>
        </div>
      </section>
    </>
  );
}
